import { useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  Image,
  Video,
  Zap,
  Camera,
  Mic,
  Music,
  DollarSign,
  Send,
  CheckCircle,
  Loader2,
  X,
  Plus,
  Play,
  Volume2,
  ShoppingCart,
  Star,
  Sparkles,
  Eye,
  Info
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Demo sample data - NO API CALLS
const DEMO_DATA = {
  sampleProducts: [
    { name: "Luxury Watch Collection", price: "2,499", description: "Swiss-made timepiece with sapphire crystal" },
    { name: "Professional Plumbing Service", price: "149", description: "24/7 emergency repairs, licensed & insured" },
    { name: "Gourmet Restaurant Menu", price: "45", description: "Farm-to-table dining experience" }
  ],
  sampleScript: `🎬 INTRODUCING: {PRODUCT_NAME}

Looking for quality you can trust? Look no further.

{DESCRIPTION}

✨ Why choose us?
• Premium quality guaranteed
• Fast, reliable service  
• Trusted by thousands

💰 Special offer: Just ${'{PRICE}'} 

📞 Order now and experience the difference!

Don't wait - this offer won't last forever.`,
  sampleImages: [
    "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600",
    "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600",
    "https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=600"
  ],
  sampleVoiceText: "This is a demo of our AI voiceover. In a live broadcast, your custom script would be narrated professionally."
};

// SelleSuite Options - No pricing here, just features
const selleSuiteOptions = [
  {
    id: "sellespot",
    name: "SelleSpot Seller",
    tagline: "Instant product broadcast",
    description: "Single product, single photo, instant broadcast. Perfect for flash sales and daily specials.",
    maxPhotos: 1,
    maxDuration: "30 sec",
    features: ["1 photo", "Price overlay", "Instant broadcast", "Buy button"],
    color: "from-amber-500 to-orange-600",
    icon: Zap
  },
  {
    id: "seller",
    name: "Seller Pro",
    tagline: "Professional product spotlight",
    description: "Spotlight your offer with multiple angles. AI voiceover describes your product professionally.",
    maxPhotos: 3,
    maxDuration: "60 sec",
    features: ["Up to 3 photos", "AI voiceover", "Price + Buy Now", "Multi-channel"],
    color: "from-primary to-purple-600",
    icon: Star,
    recommended: true
  },
  {
    id: "sellestudio",
    name: "SelleStudio Seller",
    tagline: "Full presentation suite",
    description: "Your photos. Your story. Professional slideshow with custom voice and music.",
    maxPhotos: 10,
    maxDuration: "5 min",
    features: ["Up to 10 photos", "Custom voiceover", "Background music", "Full production"],
    color: "from-accent-success to-emerald-600",
    icon: Sparkles
  }
];

// Voice options
const voiceOptions = [
  { id: "nova", name: "Nova", type: "Energetic" },
  { id: "alloy", name: "Alloy", type: "Neutral" },
  { id: "echo", name: "Echo", type: "Warm" },
  { id: "onyx", name: "Onyx", type: "Authoritative" },
  { id: "shimmer", name: "Shimmer", type: "Professional" }
];

export default function SelleSuite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  // Check if demo mode
  const isDemo = searchParams.get('demo') === 'true';
  
  // Selection state
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [step, setStep] = useState(1); // 1: Choose Suite, 2: Add Content, 3: Preview & Broadcast
  
  // Content state
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [productName, setProductName] = useState(isDemo ? DEMO_DATA.sampleProducts[0].name : "");
  const [productDescription, setProductDescription] = useState(isDemo ? DEMO_DATA.sampleProducts[0].description : "");
  const [productPrice, setProductPrice] = useState(isDemo ? DEMO_DATA.sampleProducts[0].price : "");
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [includeMusic, setIncludeMusic] = useState(true);
  
  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [createdContent, setCreatedContent] = useState(null);
  
  // Demo state
  const [selectedDemoProduct, setSelectedDemoProduct] = useState(0);
  const [showDemoVideo, setShowDemoVideo] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Handle photo upload
  const handlePhotoUpload = (files) => {
    const maxPhotos = selectedSuite ? selleSuiteOptions.find(s => s.id === selectedSuite)?.maxPhotos || 1 : 1;
    
    Array.from(files).forEach(file => {
      if (uploadedPhotos.length >= maxPhotos) {
        toast.error(`Maximum ${maxPhotos} photo(s) allowed for this option`);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload image files only");
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          file,
          preview: e.target?.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (id) => {
    setUploadedPhotos(prev => prev.filter(p => p.id !== id));
  };

  // Validation
  const canProceedToContent = () => selectedSuite !== null;
  
  const canProceedToPreview = () => {
    return uploadedPhotos.length > 0 && productName.trim() && productPrice.trim();
  };

  // Create the content
  const createContent = async () => {
    setIsProcessing(true);
    setProgress(0);
    
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 5, 90));
    }, 500);

    // DEMO MODE - No API calls, use sample data
    if (isDemo) {
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate processing
      
      clearInterval(progressInterval);
      setProgress(100);

      const demoScript = DEMO_DATA.sampleScript
        .replace('{PRODUCT_NAME}', productName)
        .replace('{DESCRIPTION}', productDescription)
        .replace('{PRICE}', productPrice);

      setCreatedContent({
        projectId: 'demo-' + Date.now(),
        script: demoScript,
        voiceUrl: null, // No real voice in demo
        photos: uploadedPhotos.length > 0 ? uploadedPhotos : DEMO_DATA.sampleImages.slice(0, selectedSuite === 'sellespot' ? 1 : selectedSuite === 'seller' ? 3 : 10).map((url, i) => ({
          id: i,
          preview: url,
          name: `demo-image-${i}.jpg`
        })),
        productName,
        productPrice,
        productDescription,
        suite: selectedSuite,
        isDemo: true
      });

      toast.success("Demo content created! (No API credits used)");
      setStep(3);
      setIsProcessing(false);
      return;
    }

    // LIVE MODE - Real API calls
    try {
      // Create project
      const projectResponse = await axios.post(`${API}/projects`, {
        name: productName,
        video_type: "sales",
        topic: productDescription || productName,
        target_duration: selectedSuite === 'sellespot' ? 30 : selectedSuite === 'seller' ? 60 : 180,
        tone: "professional"
      }, { headers: getAuthHeaders() });

      const projectId = projectResponse.data.id;

      // Generate script based on suite type
      let script = `Introducing ${productName}. ${productDescription || ''} Available now for just $${productPrice}. Order today!`;
      
      if (selectedSuite !== 'sellespot') {
        try {
          const scriptResponse = await axios.post(`${API}/videos/generate-script`, {
            video_type: "sales",
            topic: `Product: ${productName}. Description: ${productDescription}. Price: $${productPrice}. Create a professional sales pitch.`,
            target_duration: selectedSuite === 'seller' ? 60 : 180,
            tone: "professional"
          }, { headers: getAuthHeaders() });
          
          script = scriptResponse.data.script;
        } catch (e) {
          console.log("Using default script");
        }
      }

      // Generate voice if not SelleSpot
      let voiceUrl = null;
      if (selectedSuite !== 'sellespot') {
        try {
          const voiceResponse = await axios.post(`${API}/voice/generate`, {
            text: script.slice(0, 500),
            voice: selectedVoice,
            provider: "openai",
            model: "tts-1"
          }, { headers: getAuthHeaders() });
          
          if (voiceResponse.data.url) {
            voiceUrl = `${process.env.REACT_APP_BACKEND_URL}${voiceResponse.data.url}`;
          }
        } catch (e) {
          console.log("Voice generation skipped");
        }
      }

      clearInterval(progressInterval);
      setProgress(100);

      setCreatedContent({
        projectId,
        script,
        voiceUrl,
        photos: uploadedPhotos,
        productName,
        productPrice,
        productDescription,
        suite: selectedSuite,
        isDemo: false
      });

      toast.success("Content created successfully!");
      setStep(3);
    } catch (error) {
      console.error("Creation error:", error);
      toast.error("Failed to create content. Please try again.");
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  // Broadcast content
  const broadcastContent = () => {
    if (createdContent?.projectId) {
      navigate(`/broadcast/${createdContent.projectId}`);
    } else {
      navigate('/broadcast');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Demo Mode Banner */}
      {isDemo && (
        <div className="bg-accent-warning text-black py-2 px-4 text-center text-sm font-medium">
          <Eye className="w-4 h-4 inline mr-2" />
          DEMO MODE - Experience the full workflow without using any credits
          <Button 
            size="sm" 
            variant="outline" 
            className="ml-4 h-6 text-xs border-black/30 hover:bg-black/10"
            onClick={() => navigate('/suite')}
          >
            Exit Demo
          </Button>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
              className="text-text-secondary hover:text-text-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {step === 1 ? 'Back' : 'Previous'}
            </Button>
            <div className="text-center">
              <span className="font-heading font-bold text-text-primary">
                SelleSuite {isDemo && <span className="text-accent-warning">(Demo)</span>}
              </span>
              <p className="text-xs text-text-muted">Professional tools for professional sellers</p>
            </div>
            <span className="text-sm text-text-muted">Step {step} of 3</span>
          </div>
          
          {/* Progress bar */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3].map(s => (
              <div 
                key={s} 
                className={`flex-1 h-1 rounded-full transition-all ${
                  s <= step ? 'bg-primary' : 'bg-background-subtle'
                }`} 
              />
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {/* Step 1: Choose Suite */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center mb-8">
                <h1 className="font-heading font-bold text-3xl text-text-primary mb-3">
                  Choose Your Broadcast Format
                </h1>
                <p className="text-text-secondary max-w-xl mx-auto">
                  Every SelleCast feature is engineered to generate revenue.
                  Our clients don&apos;t experiment - they execute.
                </p>
                {!isDemo && (
                  <div className="flex justify-center gap-3 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-primary text-primary hover:bg-primary/10"
                      onClick={() => setShowDemoVideo(true)}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Watch Demo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-accent-warning text-accent-warning hover:bg-accent-warning/10"
                      onClick={() => navigate('/suite?demo=true')}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Try Demo (No Cost)
                    </Button>
                  </div>
                )}
              </div>

              {/* Demo Video Modal */}
              {showDemoVideo && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                  onClick={() => setShowDemoVideo(false)}
                >
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-background-paper rounded-2xl max-w-4xl w-full overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between p-4 border-b border-border">
                      <h3 className="font-heading font-bold text-lg text-text-primary">
                        SelleSuite Demo - How It Works
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowDemoVideo(false)}
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                    <div className="p-6">
                      {/* Demo Walkthrough */}
                      <div className="space-y-6">
                        <div className="aspect-video bg-gradient-to-br from-primary/20 to-accent-neon/20 rounded-xl flex items-center justify-center">
                          <div className="text-center">
                            <Play className="w-16 h-16 text-primary mx-auto mb-4 opacity-50" />
                            <p className="text-text-muted">Demo Video Placeholder</p>
                          </div>
                        </div>
                        
                        {/* Step by Step Guide */}
                        <div className="grid md:grid-cols-3 gap-4">
                          <div className="p-4 rounded-xl bg-background-subtle border border-border">
                            <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold mb-3">1</div>
                            <h4 className="font-bold text-text-primary mb-1">Choose Format</h4>
                            <p className="text-sm text-text-muted">Select SelleSpot, Seller Pro, or SelleStudio based on your needs</p>
                          </div>
                          <div className="p-4 rounded-xl bg-background-subtle border border-border">
                            <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold mb-3">2</div>
                            <h4 className="font-bold text-text-primary mb-1">Add Your Content</h4>
                            <p className="text-sm text-text-muted">Upload your product photos, add price and description</p>
                          </div>
                          <div className="p-4 rounded-xl bg-background-subtle border border-border">
                            <div className="w-10 h-10 rounded-full bg-accent-success text-white flex items-center justify-center font-bold mb-3">3</div>
                            <h4 className="font-bold text-text-primary mb-1">Broadcast & Sell</h4>
                            <p className="text-sm text-text-muted">Send to 30+ channels and start getting sales instantly</p>
                          </div>
                        </div>

                        <div className="flex justify-center gap-3 pt-4">
                          <Button
                            onClick={() => {
                              setShowDemoVideo(false);
                              navigate('/suite?demo=true');
                            }}
                            className="bg-accent-warning hover:bg-accent-warning/90 text-black"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Try Interactive Demo
                          </Button>
                          <Button
                            onClick={() => setShowDemoVideo(false)}
                            variant="outline"
                          >
                            Start Creating
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}

              <div className="grid md:grid-cols-3 gap-6">
                {selleSuiteOptions.map((option) => {
                  const IconComponent = option.icon;
                  const isSelected = selectedSuite === option.id;
                  
                  return (
                    <motion.div
                      key={option.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Card
                        onClick={() => setSelectedSuite(option.id)}
                        className={`cursor-pointer transition-all h-full relative overflow-hidden ${
                          isSelected 
                            ? 'border-2 border-primary shadow-lg shadow-primary/20' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        {option.recommended && (
                          <div className="absolute top-0 right-0 bg-primary text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                            RECOMMENDED
                          </div>
                        )}
                        
                        <CardContent className="p-6">
                          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${option.color} flex items-center justify-center text-white mb-4`}>
                            <IconComponent className="w-7 h-7" />
                          </div>
                          
                          <h3 className="font-heading font-bold text-xl text-text-primary mb-1">
                            {option.name}
                          </h3>
                          <p className="text-primary text-sm font-medium mb-3">
                            {option.tagline}
                          </p>
                          <p className="text-text-secondary text-sm mb-4">
                            {option.description}
                          </p>
                          
                          <div className="space-y-2 mb-4">
                            {option.features.map((feature, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm text-text-muted">
                                <CheckCircle className="w-4 h-4 text-accent-success" />
                                {feature}
                              </div>
                            ))}
                          </div>
                          
                          <div className="pt-4 border-t border-border flex items-center justify-between">
                            <span className="text-text-muted text-sm">Up to {option.maxDuration}</span>
                            <span className="text-text-muted text-sm">{option.maxPhotos} photo{option.maxPhotos > 1 ? 's' : ''}</span>
                          </div>
                          
                          {isSelected && (
                            <div className="absolute bottom-4 right-4">
                              <CheckCircle className="w-6 h-6 text-primary" />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>

              <div className="text-center pt-6">
                <Button
                  onClick={() => setStep(2)}
                  disabled={!canProceedToContent()}
                  size="lg"
                  className="bg-primary hover:bg-primary-hover px-12"
                >
                  Continue
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Add Content */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                  {selleSuiteOptions.find(s => s.id === selectedSuite)?.name} - Add Your Content
                </h2>
                <p className="text-text-secondary">
                  {isDemo ? "Try with sample products or enter your own" : "Your photos will be the actual presentation. Upload high-quality images."}
                </p>
              </div>

              {/* Demo Product Selector */}
              {isDemo && (
                <Card className="bg-accent-warning/10 border-accent-warning/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Info className="w-4 h-4 text-accent-warning" />
                      <Label className="text-text-primary font-medium">Try a Sample Product</Label>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {DEMO_DATA.sampleProducts.map((product, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedDemoProduct(index);
                            setProductName(product.name);
                            setProductDescription(product.description);
                            setProductPrice(product.price);
                            // Add sample images for demo
                            setUploadedPhotos(DEMO_DATA.sampleImages.slice(0, selectedSuite === 'sellespot' ? 1 : selectedSuite === 'seller' ? 3 : 10).map((url, i) => ({
                              id: i,
                              preview: url,
                              name: `sample-${i}.jpg`
                            })));
                          }}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            selectedDemoProduct === index
                              ? 'border-accent-warning bg-accent-warning/20'
                              : 'border-border hover:border-accent-warning/50'
                          }`}
                        >
                          <p className="text-sm font-medium text-text-primary truncate">{product.name}</p>
                          <p className="text-xs text-accent-success font-bold">${product.price}</p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Photo Upload */}
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-text-primary font-medium flex items-center gap-2">
                      <Camera className="w-5 h-5 text-primary" />
                      Product Photos
                    </Label>
                    <span className="text-sm text-text-muted">
                      {uploadedPhotos.length} / {selleSuiteOptions.find(s => s.id === selectedSuite)?.maxPhotos || 1}
                    </span>
                  </div>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload(e.target.files)}
                    className="hidden"
                  />
                  
                  {uploadedPhotos.length === 0 ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-all"
                    >
                      <Upload className="w-12 h-12 text-text-muted mx-auto mb-3" />
                      <p className="text-text-primary font-medium mb-1">Upload Your Product Photos</p>
                      <p className="text-text-muted text-sm">These will be your actual presentation images</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                      {uploadedPhotos.map((photo, index) => (
                        <div key={photo.id} className="relative group aspect-square">
                          <img 
                            src={photo.preview} 
                            alt="" 
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            onClick={() => removePhoto(photo.id)}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <div className="absolute bottom-1 left-1 w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                      
                      {uploadedPhotos.length < (selleSuiteOptions.find(s => s.id === selectedSuite)?.maxPhotos || 1) && (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center"
                        >
                          <Plus className="w-6 h-6 text-text-muted" />
                        </button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Product Details */}
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6 space-y-4">
                  <div>
                    <Label className="text-text-primary mb-2 block">Product / Service Name *</Label>
                    <Input
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="e.g., Premium Leather Watch, Plumbing Repair Service"
                      className="bg-background border-border"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-text-primary mb-2 block">Price *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                      <Input
                        value={productPrice}
                        onChange={(e) => setProductPrice(e.target.value)}
                        placeholder="99.99"
                        className="bg-background border-border pl-9"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-text-primary mb-2 block">Description (Optional)</Label>
                    <Textarea
                      value={productDescription}
                      onChange={(e) => setProductDescription(e.target.value)}
                      placeholder="Describe your product or service..."
                      className="bg-background border-border"
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Voice & Music Options (for Seller and SelleStudio) */}
              {selectedSuite !== 'sellespot' && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6 space-y-4">
                    <Label className="text-text-primary font-medium flex items-center gap-2">
                      <Mic className="w-5 h-5 text-primary" />
                      Voice & Audio Options
                    </Label>
                    
                    <div>
                      <Label className="text-text-secondary text-sm mb-2 block">AI Voice</Label>
                      <div className="grid grid-cols-5 gap-2">
                        {voiceOptions.map(voice => (
                          <button
                            key={voice.id}
                            onClick={() => setSelectedVoice(voice.id)}
                            className={`p-3 rounded-lg border text-center transition-all ${
                              selectedVoice === voice.id
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            <Volume2 className={`w-4 h-4 mx-auto mb-1 ${selectedVoice === voice.id ? 'text-primary' : 'text-text-muted'}`} />
                            <span className="text-xs font-medium text-text-primary block">{voice.name}</span>
                            <span className="text-xs text-text-muted">{voice.type}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {selectedSuite === 'sellestudio' && (
                      <div className="flex items-center justify-between p-3 rounded-lg bg-background-subtle">
                        <div className="flex items-center gap-2">
                          <Music className="w-4 h-4 text-text-muted" />
                          <span className="text-sm text-text-secondary">Background Music</span>
                        </div>
                        <button
                          onClick={() => setIncludeMusic(!includeMusic)}
                          className={`w-12 h-6 rounded-full transition-all ${
                            includeMusic ? 'bg-primary' : 'bg-background-subtle border border-border'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                            includeMusic ? 'translate-x-6' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Processing Indicator */}
              {isProcessing && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-8 text-center">
                    <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
                    <p className="text-text-primary font-medium mb-2">Creating your {selleSuiteOptions.find(s => s.id === selectedSuite)?.name}...</p>
                    <Progress value={progress} className="w-full max-w-xs mx-auto" />
                  </CardContent>
                </Card>
              )}

              {/* Continue Button */}
              {!isProcessing && (
                <div className="flex justify-end pt-4">
                  <Button
                    onClick={createContent}
                    disabled={!canProceedToPreview()}
                    size="lg"
                    className="bg-accent-warning hover:bg-accent-warning/90 text-black font-bold px-12"
                  >
                    Create {selleSuiteOptions.find(s => s.id === selectedSuite)?.name}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Preview & Broadcast */}
          {step === 3 && createdContent && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-success/10 border border-accent-success/30 mb-4">
                  <CheckCircle className="w-5 h-5 text-accent-success" />
                  <span className="text-accent-success font-medium">Content Created Successfully!</span>
                </div>
                <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                  Your {selleSuiteOptions.find(s => s.id === selectedSuite)?.name} is Ready
                </h2>
                <p className="text-text-secondary">Review and broadcast to your channels</p>
              </div>

              {/* Preview Card */}
              <Card className="bg-background-paper border-border overflow-hidden">
                <div className="grid md:grid-cols-2 gap-6 p-6">
                  {/* Photos */}
                  <div>
                    <Label className="text-text-primary font-medium mb-3 block">Product Photos</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {createdContent.photos.map((photo, i) => (
                        <div key={photo.id} className="aspect-square rounded-lg overflow-hidden">
                          <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Details */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-text-muted text-sm">Product</Label>
                      <p className="text-text-primary font-bold text-xl">{createdContent.productName}</p>
                    </div>
                    
                    <div>
                      <Label className="text-text-muted text-sm">Price</Label>
                      <p className="text-accent-success font-bold text-2xl">${createdContent.productPrice}</p>
                    </div>
                    
                    {createdContent.productDescription && (
                      <div>
                        <Label className="text-text-muted text-sm">Description</Label>
                        <p className="text-text-secondary">{createdContent.productDescription}</p>
                      </div>
                    )}
                    
                    {createdContent.voiceUrl && (
                      <div>
                        <Label className="text-text-muted text-sm mb-2 block">Voice Narration</Label>
                        <audio controls className="w-full">
                          <source src={createdContent.voiceUrl} type="audio/mpeg" />
                        </audio>
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              {/* Script Preview */}
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6">
                  <Label className="text-text-primary font-medium mb-3 block">Generated Script</Label>
                  <p className="text-text-secondary text-sm leading-relaxed whitespace-pre-line">
                    {createdContent.script}
                  </p>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="grid md:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/library')}
                  className="py-6"
                >
                  <Image className="w-5 h-5 mr-2" />
                  Save to Library
                </Button>
                
                <Button
                  size="lg"
                  onClick={broadcastContent}
                  className="py-6 bg-primary hover:bg-primary-hover"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Broadcast Now
                </Button>
                
                <Button
                  size="lg"
                  onClick={() => {
                    setStep(1);
                    setSelectedSuite(null);
                    setUploadedPhotos([]);
                    setProductName("");
                    setProductPrice("");
                    setProductDescription("");
                    setCreatedContent(null);
                  }}
                  variant="outline"
                  className="py-6"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Another
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
